import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx } from './cx';

export type ButtonVariant = 'default' | 'primary' | 'flame' | 'ghost';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  icon?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

/**
 * Returns the class string that the Button primitive would apply. Use this for
 * elements that can't be a `<button>` (e.g. `<a href>`, react-router `<Link>`)
 * but should look identical. Keeps the variant→class mapping in one file.
 */
export function buttonClassName(opts: {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  icon?: boolean;
  className?: string;
} = {}): string {
  const { variant, size, icon, className } = opts;
  return cx(
    'mf-btn',
    variant === 'primary' && 'primary',
    variant === 'flame' && 'flame',
    variant === 'ghost' && 'ghost',
    size === 'sm' && 'sm',
    icon && 'icon',
    className,
  );
}

/**
 * Button primitive backed by the `.mf-btn` classes defined in @emailforge/tokens.
 * Keeps styling centralized so screens don't spell out class combinations ad-hoc.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', icon, loading, leftIcon, rightIcon, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cx(
        'mf-btn',
        variant === 'primary' && 'primary',
        variant === 'flame' && 'flame',
        variant === 'ghost' && 'ghost',
        size === 'sm' && 'sm',
        icon && 'icon',
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});
